import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useCreateSaleMutation } from '../slices/salesApiSlice';
import { useGetCustomersQuery } from '../slices/customersApiSlice';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const SalesForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // NORMALIZER
  const normalizeCartItem = (item) => ({
    ...item,
    product: item.product || item._id,
    images: Array.isArray(item.images) ? item.images : [item.images],
    countInStock: item.countInStock || 9999,
  });
  
  const { userInfo } = useSelector((state) => state.auth);

  const rawCartItems = useSelector((state) => state.cart.cartItems);

  const cartItems = useMemo(() => {
    return rawCartItems.map(normalizeCartItem);
  }, [rawCartItems]);

  // ⭐ NEW: SEARCH STATE
  const [searchTerm, setSearchTerm] = useState('');

  // ⭐ NEW: FILTERED ITEMS
  const filteredCartItems = useMemo(() => {
    if (!searchTerm.trim()) return cartItems;

    return cartItems.filter((item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(item.price).includes(searchTerm) ||
      String(item.product).includes(searchTerm)
    );
  }, [searchTerm, cartItems]);

  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [note, setNote] = useState('');
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);

  const { data: customersData, isLoading, error } =
    useGetCustomersQuery({ keyword: customerSearch });

  const [createSale, { isLoading: savingSales, error: salesError }] =
    useCreateSaleMutation();

  // Filter customers
  useEffect(() => {
    if (customersData?.customers) {
      const filtered = customersData.customers.filter((customer) =>
        customer.name.toLowerCase().includes(customerSearch.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearch, customersData]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setFilteredCustomers([]);
  };

  const addToCartHandler = (item, qty) => {
    dispatch(addToCart({ ...item, qty }));
  };

  const removeFromCartHandler = (productId) => {
    dispatch(removeFromCart(productId));
  };

  const placeSaleHandler = async () => {
    try {
      if (!selectedCustomer) {
        toast.error('Please select a customer');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      const salesData = {
        saleItems: cartItems.map((item) => ({
          name: item.name,
          qty: item.qty,
          price: item.price,
          images: item.images[0],
          product: item.product,
        })),
        customerDetails: selectedCustomer._id,
        note,
        date: saleDate,
      };

      await createSale(salesData).unwrap();
      dispatch(clearCartItems());
      toast.success('Sale successfully recorded');
      navigate('/house/HomeScreen');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to record sales');
    }
  };

  const exportSalesToExcel = () => {
    if (cartItems.length === 0) {
      toast.warn('No items to export');
      return;
    }

    const exportData = cartItems.map((item) => ({
      Product: item.name,
      Quantity: item.qty,
      Price: item.price,
      Total: (item.qty * item.price).toFixed(2),
      Image: item.images?.[0] || '',
      ProductID: item.product,
      Customer: selectedCustomer ? selectedCustomer.name : '',
      CustomerCompany: selectedCustomer ? selectedCustomer.company : '',
      CustomerPhone: selectedCustomer ? selectedCustomer.telephone : '',
      Date: saleDate,
      Note: note,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales');

    XLSX.writeFile(workbook, `Sales_${saleDate}.xlsx`);
  };
  const handlePrint = () => {
  const businessName = "Sutuko Clinic";
  const businessAddress = "Sutukoba,Uppper River Region, The Gambia";
  const businessPhone = "+2207050130";
  const thankYouMessage = "Thank you for your business!";
  const logoUrl = "/images/sclinic.jpeg";

  const cashierName = userInfo?.name || "Cashier";
  //const cashierId = userInfo?._id;

  const customerName = selectedCustomer ? selectedCustomer.name : "Unknown Customer";

  // ⭐ Auto Date + Time
  const now = new Date();
  const formattedDate = now.toLocaleDateString();
  const formattedTime = now.toLocaleTimeString();

  // ⭐ Random Invoice Number
  const invoiceNumber = Math.floor(100000 + Math.random() * 900000);

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.qty * item.price,
    0
  );

  const printableContent = `
    <html>
      <head>
        <title>Print Receipt</title>
        <style>
          @media print {
            body {
              width: 70mm;
              margin: 0 auto;
              font-family: Arial, sans-serif;
              font-size: 12px;
              color: #333;
            }
          }

          .header {
            text-align: center;
            margin-bottom: 10px;
          }

          .logo {
            width: 60px;
            height: auto;
            margin-bottom: 5px;
          }

          h1 {
            margin: 0;
            font-size: 16px;
          }

          h3 {
            margin-top: 5px;
            font-size: 12px;
          }

          .meta {
            font-size: 11px;
            margin-top: 5px;
            text-align: center;
            line-height: 1.4;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }

          th, td {
            border: 1px solid #ccc;
            padding: 4px;
            font-size: 11px;
          }

          th {
            background: #f0f0f0;
          }

          .subtotal {
            margin-top: 10px;
            font-size: 14px;
            font-weight: bold;
            text-align: right;
          }

          .note {
            margin-top: 10px;
            font-size: 12px;
            font-style: italic;
          }

          .footer {
            margin-top: 20px;
            text-align: center;
            font-size: 11px;
            color: #555;
          }

          .thankyou {
            margin-top: 5px;
            font-size: 13px;
            font-weight: bold;
            color: #222;
          }
        </style>
      </head>

      <body>
        <div class="header">
          <img src="${logoUrl}" class="logo" alt="Business Logo" />
          <h1>${businessName}</h1>
          <h3>Customer: ${customerName}</h3>
        </div>
          
         <div class="meta">
            Invoice No: <strong>${invoiceNumber}</strong><br/>
            Date: ${formattedDate}<br/>
            Time: ${formattedTime}<br/>
            Cashier: <strong>${cashierName}</strong><br/>
            
          </div>

        
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${cartItems
              .map(
                (item) => `
              <tr>
                <td>${item.name}</td>
                <td>${item.qty}</td>
                <td>D${item.price}</td>
                <td>D${(item.qty * item.price).toFixed(2)}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <div class="subtotal">
          Subtotal: D${subtotal.toFixed(2)}
        </div>

        ${
          note
            ? `<div class="note">Note: ${note}</div>`
            : ""
        }

        <div class="footer">
          <div>${businessName}</div>
          <div>${businessAddress}</div>
          <div>Phone: ${businessPhone}</div>
          <div class="thankyou">${thankYouMessage}</div>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open("", "", "width=400,height=600");
  printWindow.document.write(printableContent);
  printWindow.document.close();
  printWindow.print();
};


  return (
    <Row>
      <Col md={8}>
        <h1>Sales Invoice</h1>

        {/* ⭐ NEW: SEARCH BAR */}
        <Form.Group className="mb-3">
          <Form.Label>Search Items</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search by name, price, or product ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Form.Group>

        {/* ⭐ If no items match search */}
        {filteredCartItems.length === 0 ? (
          <Message>No items match your search.</Message>
        ) : (
          <ListGroup variant="flush">
            {filteredCartItems.map((item) => (
              <ListGroup.Item key={item.product}>
                <Row>
                  <Col md={2}>
                    <img
                      src={item.images[0]}
                      alt="Product Thumbnail"
                      style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                    />
                  </Col>

                  <Col>{item.name}</Col>

                  <Col md={2}>D{item.price}</Col>

                  <Col md={2}>
                    <Form.Control
                      as="select"
                      value={item.qty}
                      onChange={(e) =>
                        addToCartHandler(item, Number(e.target.value))
                      }
                    >
                      {[...Array(item.countInStock).keys()].map((x) => (
                        <option key={x + 1} value={x + 1}>
                          {x + 1}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>

                  <Col md={2}>D{(item.qty * item.price).toFixed(2)}</Col>

                  <Col md={1}>
                    <Button
                      variant="light"
                      onClick={() => removeFromCartHandler(item.product)}
                    >
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>
                Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)})
                items
              </h2>
              D
              {cartItems
                .reduce((acc, item) => acc + item.qty * item.price, 0)
                .toFixed(2)}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2>Select Customer</h2>

              {isLoading ? (
                <Loader />
              ) : error ? (
                <Message variant="danger">{error?.data?.message}</Message>
              ) : (
                <>
                  <Form.Group controlId="customerSearch">
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type customer name"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                    />
                  </Form.Group>

                  {filteredCustomers.length > 0 && (
                    <ListGroup className="dropdown-menu mt-2">
                      {filteredCustomers.map((customer) => (
                        <ListGroup.Item
                          key={customer._id}
                          action
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customer.name} ({customer.company})
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}

                  {selectedCustomer && (
                    <div className="mt-3">
                      <strong>Selected Customer:</strong>
                      <p>
                        {selectedCustomer.name} ({selectedCustomer.company})<br />
                        Phone: {selectedCustomer.telephone}<br />
                        Address: {selectedCustomer.address}<br />
                        SERIAL: {selectedCustomer.serialNumber}
                      </p>

                      <Form.Group controlId="note">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter additional notes"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </Form.Group>

                      <Form.Group controlId="saleDate" className="mt-2">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={saleDate}
                          onChange={(e) => setSaleDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </ListGroup.Item>

            {salesError && (
              <ListGroup.Item>
                <Message variant="danger">{salesError.message}</Message>
              </ListGroup.Item>
            )}

            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block"
                disabled={cartItems.length === 0 || !selectedCustomer}
                onClick={placeSaleHandler}
              >
                Submit Sales
              </Button>
              {savingSales && <Loader />}
            </ListGroup.Item>

            <ListGroup.Item>
              <Button
                type="button"
                className="btn-block mb-2"
                variant="success"
                disabled={cartItems.length === 0}
                onClick={exportSalesToExcel}
              >
                Export Sales to Excel
              </Button>
              <Button
             variant="secondary"
             className="btn-block mb-2"
          
            onClick={handlePrint}
             >
             Print Receipt
            </Button>

            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default SalesForm;
